const AGE_EVENT = 'dd:age-acknowledged';
const PRIVACY_EVENT = 'visitorConsentCollected';
const STATE_EVENT = 'dd:integration-state-changed';
const PURPOSES = ['analytics', 'marketing', 'preferences', 'sale_of_data'];
const OVERLAY_SELECTOR = [
  '.smile-launcher-frame-container',
  '.smile-panel-frame-container',
  'iframe[src*="smile.io" i]',
  'iframe[src*="omnisend" i]',
  '[class*="omnisend" i]',
  '[id*="omnisend" i]',
].join(',');

/** @type {Map<HTMLElement, {inert: boolean, ariaHidden: string | null}>} */
const containedOverlays = new Map();

// The Theme Editor and password layout never render the age gate;
// snippets/scripts.liquid stamps data-dd-age-exempt on <html> there so
// overlays are not suppressed forever.
const state = {
  ageAcknowledged:
    document.documentElement.dataset.ddAgeExempt === 'true'
    || document.documentElement.classList.contains('dd-age-verified'),
  privacyAvailable: false,
  purposes: {
    analytics: null,
    marketing: null,
    preferences: null,
    sale_of_data: null,
  },
};

/** @param {unknown} value */
function normalizeConsent(value) {
  if (value === true || value === 'yes' || value === 'allowed' || value === 'granted') return true;
  if (value === false || value === 'no' || value === 'denied') return false;
  return null;
}

/** @param {Record<string, unknown>} api @param {Record<string, unknown>} current @param {string} purpose */
function readPurpose(api, current, purpose) {
  const methodNames = {
    analytics: 'analyticsProcessingAllowed',
    marketing: 'marketingAllowed',
    preferences: 'preferencesProcessingAllowed',
    sale_of_data: 'saleOfDataAllowed',
  };
  const method = api[methodNames[purpose]];
  if (typeof method === 'function') {
    try {
      return normalizeConsent(method.call(api));
    } catch (_error) {
      return null;
    }
  }
  return normalizeConsent(current[purpose]);
}

function refreshPrivacyState() {
  const api = window.Shopify?.customerPrivacy;
  if (!api || typeof api.currentVisitorConsent !== 'function') {
    state.privacyAvailable = false;
    for (const purpose of PURPOSES) state.purposes[purpose] = null;
    publishState();
    return;
  }

  try {
    const current = api.currentVisitorConsent() || {};
    state.privacyAvailable = true;
    for (const purpose of PURPOSES) state.purposes[purpose] = readPurpose(api, current, purpose);
  } catch (_error) {
    state.privacyAvailable = false;
    for (const purpose of PURPOSES) state.purposes[purpose] = null;
  }
  publishState();
}

function publishState() {
  const root = document.documentElement;
  root.dataset.ddAgeAcknowledged = String(state.ageAcknowledged);
  root.dataset.ddPrivacyApi = state.privacyAvailable ? 'available' : 'unavailable';
  for (const purpose of PURPOSES) {
    const value = state.purposes[purpose];
    root.setAttribute(
      `data-dd-consent-${purpose.replaceAll('_', '-')}`,
      value === null ? 'unknown' : value ? 'allowed' : 'denied'
    );
  }
  document.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: snapshot() }));
}

function snapshot() {
  return {
    ageAcknowledged: state.ageAcknowledged,
    privacyAvailable: state.privacyAvailable,
    purposes: { ...state.purposes },
  };
}

/** @param {HTMLElement} element */
function isOverlay(element) {
  if (element.matches('.smile-launcher-frame-container, .smile-panel-frame-container, iframe')) return true;
  if (element.getAttribute('role') === 'dialog' || element.getAttribute('aria-modal') === 'true') return true;
  // fixed only: the broad [class*="omnisend"] selectors also match non-modal
  // app elements, and inerting a merely-sticky one hides real page UI.
  return getComputedStyle(element).position === 'fixed';
}

/** @param {ParentNode} root */
function containKnownOverlays(root = document) {
  if (state.ageAcknowledged) return;
  const matches = [];
  if (root instanceof HTMLElement && root.matches(OVERLAY_SELECTOR)) matches.push(root);
  matches.push(...root.querySelectorAll(OVERLAY_SELECTOR));

  for (const match of matches) {
    if (!(match instanceof HTMLElement) || containedOverlays.has(match) || !isOverlay(match)) continue;
    containedOverlays.set(match, {
      inert: match.hasAttribute('inert'),
      ariaHidden: match.getAttribute('aria-hidden'),
    });
    match.classList.add('dd-age-contained-overlay');
    match.setAttribute('inert', '');
    match.setAttribute('aria-hidden', 'true');
  }
}

function releaseKnownOverlays() {
  for (const [element, previous] of containedOverlays) {
    element.classList.remove('dd-age-contained-overlay');
    if (!previous.inert) element.removeAttribute('inert');
    if (previous.ariaHidden === null) element.removeAttribute('aria-hidden');
    else element.setAttribute('aria-hidden', previous.ariaHidden);
  }
  containedOverlays.clear();
}

const overlayObserver = new MutationObserver((mutations) => {
  if (state.ageAcknowledged) return;
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) containKnownOverlays(node);
    }
  }
});

document.addEventListener(AGE_EVENT, () => {
  state.ageAcknowledged = true;
  overlayObserver.disconnect();
  releaseKnownOverlays();
  publishState();
});

document.addEventListener(PRIVACY_EVENT, refreshPrivacyState);

window.DDIntegrationState = Object.freeze({
  eventName: STATE_EVENT,
  snapshot,
  refresh: refreshPrivacyState,
  isAllowed(purpose) {
    if (!state.ageAcknowledged || !state.privacyAvailable || !PURPOSES.includes(purpose)) return false;
    return state.purposes[purpose] === true;
  },
});

if (!state.ageAcknowledged) {
  containKnownOverlays();
  overlayObserver.observe(document.documentElement, { childList: true, subtree: true });
}

refreshPrivacyState();
window.addEventListener('load', refreshPrivacyState, { once: true });
